import {
  ActionIcon, Alert, Badge, Button, Card, Checkbox, Divider, Group, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Text,
  Textarea, TextInput, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconDownload, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { downloadFile, http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, fmtNum, StatusBadge } from "../../components/ui";

// upis bez vremena (npr. berba iz uvoza) čuva se kao ponoć UTC — prikazuje se samo datum, ne „02:00“
function fmtStart(v: unknown): string {
  const s = String(v ?? "");
  if (/T00:00(:00)?(\.0+)?(Z|\+00:00)$/.test(s)) return new Date(s).toLocaleDateString("sr-Latn-BA", { timeZone: "UTC" });
  return fmtDateTime(v).slice(0, -3);
}
import { useT } from "../../i18n/script";
import { dosePerHa, expired, preparatUnit, primjenaFor, registeredFor } from "./ffs";
import type { Orchard } from "./OrchardDetailPage";

/** Stavka plana radova (iz tehnološkog postupka) kojom se popunjava novi upis. */
export interface PlanPreset {
  id: number; entry_type: string; title: string; description?: string | null; condition?: string | null; bbch_from?: number | null;
  part_of_orchard?: string | null; water_l_ha?: number | null; irrigation?: { sistem?: string; kolicina_m3_ha?: number } | null;
  products: { vrsta: "ffs" | "dubrivo"; sifra?: string | null; naziv: string; doza?: number | null; jedinica?: string | null; cilj?: string | null;
    karenca_dana?: number | null }[];
}

interface Item { code: string; name: string; attributes: Record<string, unknown> }
interface Entry {
  id: number; entry_type: string; start_at: string; end_at?: string; bbch_code?: number; area_ha?: number; operator_name?: string;
  operator_cert_no?: string; labour_hours?: number; workers?: number; cost_km?: number; description?: string; status: string;
  storno_reason?: string; recorded_at: string; created_by: string;
  ppp: { product_name: string; registration_no?: string; dose: number; dose_unit: string; target_organism?: string; phi_days?: number; earliest_harvest?: string }[];
  fertilization: { product_name: string; quantity_per_ha: number; unit: string; n_kg_ha?: number }[];
  irrigation?: { volume_m3?: number; depth_mm?: number; system?: string } | null;
}

export const useItems = (code: string) =>
  useQuery({ queryKey: ["codelist", code], staleTime: 600_000, queryFn: () => http.get<Item[]>(`/api/v1/codelists/${code}/items`) });

const local = (d = new Date()) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

interface Ppp { product_code: string | null; product_name: string; registration_no: string; active_substances: string; target_organism: string | null;
  justification: string; dose: number | string; dose_unit: string; water_l_ha: number | string; phi_days: number | string }
interface Fert { product_code: string | null; product_name: string; fertilizer_type: string; n_pct: number | string; p2o5_pct: number | string;
  k2o_pct: number | string; quantity_per_ha: number | string; unit: string; method: string }

export function EntryModal({ orchard, onClose, initialType = "zastita", preset }: {
  orchard: Orchard; onClose: () => void; initialType?: string; preset?: PlanPreset | null }) {
  const { t } = useT();
  const qc = useQueryClient();
  const types = useQuery({ queryKey: ["entry-types"], queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/diary/entry-types") });
  const ffs = useItems("ffs"), org = useItems("stetni_organizam"), fert = useItems("dubrivo"), src = useItems("izvor_vode"), sys = useItems("sistem_navodnjavanja");
  const species = orchard.species_all?.length ? orchard.species_all : [orchard.species];
  const [type, setType] = useState<string | null>(preset?.entry_type ?? initialType);
  const [c, setC] = useState({ start_at: local(), end_at: local(new Date(Date.now() + 2 * 3600e3)), bbch_code: (preset?.bbch_from ?? "") as number | string,
    area_ha: orchard.area_ha as number | string, part_of_orchard: preset?.part_of_orchard ?? "", operator_name: "", operator_cert_no: "", machine: "",
    labour_hours: "" as number | string, workers: "" as number | string, cost_km: "" as number | string,
    description: preset ? `${t("Plan radova")}: ${preset.title}` : "" });
  const emptyPpp: Ppp = { product_code: null, product_name: "", registration_no: "", active_substances: "", target_organism: null, justification: "",
    dose: "", dose_unit: "l/ha", water_l_ha: "", phi_days: "" };
  const [ppp, setPpp] = useState<Ppp[]>([emptyPpp]);
  const emptyFert: Fert = { product_code: null, product_name: "", fertilizer_type: "mineralno", n_pct: "", p2o5_pct: "", k2o_pct: "",
    quantity_per_ha: "", unit: "kg/ha", method: "rasipanje" };
  const [ferts, setFerts] = useState<Fert[]>([emptyFert]);
  const [irr, setIrr] = useState({ water_source: null as string | null, system: null as string | null, volume_m3: "" as number | string,
    depth_mm: "" as number | string, duration_h: "" as number | string, fertigation: false });
  const [warn, setWarn] = useState<string[]>([]);
  const n = (v: number | string) => (v === "" || v === null ? null : Number(v));
  // popunjavanje iz plana radova kad se učitaju šifarnici (doza iz postupka preračunata na količinu po ha)
  useEffect(() => {
    if (!preset || !ffs.data || !fert.data) return;
    const water = preset.water_l_ha ?? 1000;
    const pp = preset.products.filter((x) => x.vrsta === "ffs").map((m): Ppp => {
      const it = ffs.data!.find((x) => x.code === m.sifra);
      const a = (it?.attributes ?? {}) as Record<string, unknown>;
      const unit = preparatUnit(a);
      const dose = m.doza == null ? "" : m.jedinica === "%" ? Math.round(m.doza * water * 10) / 1000 : m.doza;
      return { ...emptyPpp, product_code: it ? m.sifra ?? null : null, product_name: it?.name ?? m.naziv,
        registration_no: String(a.registracija ?? ""), active_substances: String(a.aktivna_materija ?? ""), target_organism: null,
        justification: preset.condition ?? "", dose, dose_unit: m.jedinica === "%" || !m.jedinica ? unit : m.jedinica,
        water_l_ha: water, phi_days: m.karenca_dana ?? primjenaFor(a, species)?.karenca_dana ?? "" };
    });
    if (pp.length) setPpp(pp);
    const ff = preset.products.filter((x) => x.vrsta === "dubrivo").map((m): Fert => {
      const it = fert.data!.find((x) => x.code === m.sifra);
      const a = (it?.attributes ?? {}) as Record<string, number | string>;
      return { product_code: it ? m.sifra ?? null : null, product_name: it?.name ?? m.naziv, fertilizer_type: String(a.tip ?? "mineralno"),
        n_pct: (a.n_pct as number) ?? "", p2o5_pct: (a.p2o5_pct as number) ?? "", k2o_pct: (a.k2o_pct as number) ?? "",
        quantity_per_ha: m.doza ?? "", unit: m.jedinica && ["kg/ha", "l/ha", "t/ha"].includes(m.jedinica) ? m.jedinica : "kg/ha",
        method: a.tip === "folijarno" ? "folijarno" : "rasipanje" };
    });
    if (ff.length) setFerts(ff);
    if (preset.irrigation?.kolicina_m3_ha) {
      const m3 = Math.round(preset.irrigation.kolicina_m3_ha * (Number(orchard.area_ha) || 1) * 10) / 10;
      setIrr((x) => ({ ...x, volume_m3: m3, depth_mm: Math.round(preset.irrigation!.kolicina_m3_ha! / 10 * 10) / 10 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.id, !!ffs.data, !!fert.data]);
  const ffsOptions = (() => {
    const reg: { value: string; label: string }[] = [], other: { value: string; label: string }[] = [];
    for (const x of ffs.data ?? []) {
      const a = x.attributes;
      const r = registeredFor(a, species);
      const lbl = `${x.name}${a.aktivna_materija ? ` — ${a.aktivna_materija}` : ""}${expired(a) ? ` (${t("istekla registracija")})` : ""}`;
      (r === false ? other : reg).push({ value: x.code, label: lbl });
    }
    return [{ group: t("Registrovano za ovu vrstu"), items: reg }, { group: t("Ostala sredstva (nisu registrovana za ovu vrstu)"), items: other }]
      .filter((g) => g.items.length);
  })();
  const save = useMutation({
    mutationFn: () => http.post<{ id: number; warnings?: string[] }>(`/api/v1/diary/orchards/${orchard.id}/entries`, {
      entry_type: type, start_at: c.start_at, end_at: c.end_at || null, bbch_code: n(c.bbch_code), area_ha: n(c.area_ha),
      part_of_orchard: c.part_of_orchard || null, operator_name: c.operator_name || null, operator_cert_no: c.operator_cert_no || null,
      machine: c.machine || null, labour_hours: n(c.labour_hours), workers: n(c.workers), cost_km: n(c.cost_km), description: c.description || null,
      ppp: type === "zastita" ? ppp.map((p) => ({ ...p, dose: n(p.dose), water_l_ha: n(p.water_l_ha), phi_days: n(p.phi_days),
        registration_no: p.registration_no || null, active_substances: p.active_substances || null })) : [],
      fertilization: type === "dubrenje" ? ferts.map((f) => ({ ...f, n_pct: n(f.n_pct), p2o5_pct: n(f.p2o5_pct), k2o_pct: n(f.k2o_pct),
        quantity_per_ha: n(f.quantity_per_ha) })) : [],
      irrigation: type === "navodnjavanje" ? { ...irr, volume_m3: n(irr.volume_m3), depth_mm: n(irr.depth_mm), duration_h: n(irr.duration_h) } : null,
      plan_task_id: preset?.id ?? null,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["diary"] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      if (r.warnings?.length) setWarn(r.warnings);
      else { notifications.show({ color: "green", message: t("Upis sačuvan") }); onClose(); }
    },
    onError: (e: Error) => notifications.show({ color: "red", title: t("Upis nije prihvaćen"), message: e.message }),
  });
  const setP = (i: number, patch: Partial<Ppp>) => setPpp(ppp.map((p, j) => (i === j ? { ...p, ...patch } : p)));
  const setF = (i: number, patch: Partial<Fert>) => setFerts(ferts.map((p, j) => (i === j ? { ...p, ...patch } : p)));
  const cc = (k: keyof typeof c) => ({ value: c[k] as string, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setC({ ...c, [k]: e.currentTarget.value }) });
  const cn = (k: keyof typeof c) => ({ value: c[k] as number | string, onChange: (v: number | string) => setC({ ...c, [k]: v }) });
  return (
    <Modal opened onClose={onClose} size="xl" title={t("Novi upis u poljoprivrednu knjigu")}>
      <Stack>
        {warn.length > 0 ? (
          <>
            <Alert color="orange" icon={<IconAlertTriangle />} title={t("Upis je sačuvan uz upozorenja")}>
              {warn.map((w) => <Text key={w} size="sm">• {w}</Text>)}
            </Alert>
            <Group justify="flex-end"><Button onClick={onClose}>{t("Zatvori")}</Button></Group>
          </>
        ) : (
          <>
            {preset && (
              <Alert color="teal" variant="light" title={`${t("Iz plana radova")}: ${preset.title}`}>
                {preset.condition && <Text size="sm"><b>{t("Uslov")}:</b> {preset.condition}</Text>}
                {preset.description && <Text size="xs" c="dimmed" mt={4}>{preset.description}</Text>}
              </Alert>
            )}
            <Select label={t("Radna operacija")} data={(types.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={type} onChange={setType} required />
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TextInput type="datetime-local" label={t("Početak")} required {...cc("start_at")} />
              <TextInput type="datetime-local" label={t("Završetak")} required={type === "zastita"} {...cc("end_at")} />
              <NumberInput label={t("BBCH faza")} min={0} max={99} {...cn("bbch_code")} />
              <NumberInput label={t("Površina (ha)")} decimalScale={4} decimalSeparator="," {...cn("area_ha")} />
              <TextInput label={t("Dio voćnjaka (redovi/tabla)")} {...cc("part_of_orchard")} />
              <TextInput label={t("Mehanizacija")} {...cc("machine")} />
              <TextInput label={t("Izvršilac")} required={type === "zastita"} {...cc("operator_name")} />
              <TextInput label={t("Broj certifikata (FFS)")} {...cc("operator_cert_no")} />
              <NumberInput label={t("Radni sati")} decimalSeparator="," {...cn("labour_hours")} />
              <NumberInput label={t("Broj radnika")} {...cn("workers")} />
              <NumberInput label={t("Trošak (KM)")} decimalSeparator="," {...cn("cost_km")} />
            </SimpleGrid>
            {type === "zastita" && (
              <>
                <Divider label={t("Sredstva za zaštitu bilja (BiH 101/12, Prilog I)")} />
                {ppp.map((p, i) => (
                  <Card key={i} withBorder p="sm">
                    <SimpleGrid cols={{ base: 1, sm: 3 }}>
                      <Select label={t("Sredstvo (Jedinstveni spisak FFS)")} searchable clearable data={ffsOptions}
                        value={p.product_code} onChange={(v) => {
                          const it = ffs.data?.find((x) => x.code === v);
                          const a = (it?.attributes ?? {}) as Record<string, unknown>;
                          const prim = primjenaFor(a, species);
                          const d = dosePerHa(prim, a, Number(p.water_l_ha) || 1000);
                          setP(i, { product_code: v, product_name: it?.name ?? "", registration_no: String(a.registracija ?? ""),
                            active_substances: String(a.aktivna_materija ?? ""), dose_unit: d.unit,
                            phi_days: prim?.karenca_dana ?? (a.karenca_dana as number) ?? "", dose: d.dose,
                            target_organism: p.target_organism, justification: p.justification });
                        }} />
                      <TextInput label={t("Trgovački naziv")} required value={p.product_name} onChange={(e) => setP(i, { product_name: e.currentTarget.value })} />
                      <TextInput label={t("Broj registracije")} value={p.registration_no} onChange={(e) => setP(i, { registration_no: e.currentTarget.value })} />
                      <TextInput label={t("Aktivna materija")} value={p.active_substances} onChange={(e) => setP(i, { active_substances: e.currentTarget.value })} />
                      <Select label={t("Štetni organizam")} searchable clearable data={(org.data ?? []).map((x) => x.name)} value={p.target_organism}
                        onChange={(v) => setP(i, { target_organism: v })} />
                      <TextInput label={t("Opravdanost (IPM)")} placeholder={t("prognozna služba / prag / monitoring")} value={p.justification}
                        onChange={(e) => setP(i, { justification: e.currentTarget.value })} />
                      <NumberInput label={t("Doza po ha")} required decimalScale={4} decimalSeparator="," value={p.dose} onChange={(v) => setP(i, { dose: v })} />
                      <Select label={t("Jedinica")} data={["l/ha", "kg/ha", "ml/ha", "g/ha"]} value={p.dose_unit} onChange={(v) => setP(i, { dose_unit: v ?? "l/ha" })} />
                      <NumberInput label={t("Karenca (dana)")} value={p.phi_days} onChange={(v) => setP(i, { phi_days: v })} />
                      <NumberInput label={t("Utrošak vode (l/ha)")} min={0} max={3000} value={p.water_l_ha} onChange={(v) => setP(i, { water_l_ha: v })} />
                    </SimpleGrid>
                    {(() => {
                      const it = ffs.data?.find((x) => x.code === p.product_code);
                      if (!it) return null;
                      const prim = primjenaFor(it.attributes, species);
                      return (
                        <Text size="xs" c={prim ? "dimmed" : "orange"} mt={6}>
                          {prim ? `${t("Etiketa")} (${prim.kultura}): ${prim.doza ?? "—"}${prim.karenca_dana != null ? ` · ${t("karenca")} ${prim.karenca_dana} d` : prim.karenca_tekst ? ` · ${t("karenca")} ${prim.karenca_tekst}` : ""}${prim.max_tretmana ? ` · ${t("najviše")} ${prim.max_tretmana}×` : ""}`
                            : t("Za ovu vrstu nema registrovane primjene u Jedinstvenom spisku FFS — provjerite etiketu.")}
                          {/* latinski nazivi organizama se ne transliterišu */}
                          {prim?.organizmi ? <> · <i translate="no">{prim.organizmi}</i></> : null}
                          {it.attributes.vazi_do ? ` · ${t("registracija važi do")} ${String(it.attributes.vazi_do)}` : ""}
                        </Text>
                      );
                    })()}
                    {ppp.length > 1 && <Button mt="xs" size="compact-xs" color="red" variant="subtle" leftSection={<IconTrash size={12} />}
                      onClick={() => setPpp(ppp.filter((_, j) => j !== i))}>{t("Ukloni")}</Button>}
                  </Card>
                ))}
                <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} w={230} onClick={() => setPpp([...ppp, emptyPpp])}>{t("Dodaj sredstvo (mješavina)")}</Button>
              </>
            )}
            {type === "dubrenje" && (
              <>
                <Divider label={t("Đubriva")} />
                {ferts.map((f, i) => (
                  <Card key={i} withBorder p="sm">
                    <SimpleGrid cols={{ base: 1, sm: 4 }}>
                      <Select label={t("Đubrivo")} searchable clearable data={(fert.data ?? []).map((x) => ({ value: x.code, label: x.name }))}
                        value={f.product_code} onChange={(v) => {
                          const it = fert.data?.find((x) => x.code === v);
                          const a = (it?.attributes ?? {}) as Record<string, string | number>;
                          setF(i, { product_code: v, product_name: it?.name ?? "", fertilizer_type: String(a.tip ?? "mineralno"),
                            n_pct: (a.n_pct as number) ?? "", p2o5_pct: (a.p2o5_pct as number) ?? "", k2o_pct: (a.k2o_pct as number) ?? "" });
                        }} />
                      <TextInput label={t("Naziv")} required value={f.product_name} onChange={(e) => setF(i, { product_name: e.currentTarget.value })} />
                      <NumberInput label={t("Količina po ha")} required decimalSeparator="," value={f.quantity_per_ha} onChange={(v) => setF(i, { quantity_per_ha: v })} />
                      <Select label={t("Jedinica")} data={["kg/ha", "l/ha", "t/ha"]} value={f.unit} onChange={(v) => setF(i, { unit: v ?? "kg/ha" })} />
                      <NumberInput label="N %" decimalSeparator="," value={f.n_pct} onChange={(v) => setF(i, { n_pct: v })} />
                      <NumberInput label="P₂O₅ %" decimalSeparator="," value={f.p2o5_pct} onChange={(v) => setF(i, { p2o5_pct: v })} />
                      <NumberInput label="K₂O %" decimalSeparator="," value={f.k2o_pct} onChange={(v) => setF(i, { k2o_pct: v })} />
                      <Select label={t("Način")} data={["rasipanje", "fertirigacija", "folijarno", "unošenje u zonu korijena"]} value={f.method} onChange={(v) => setF(i, { method: v ?? "" })} />
                    </SimpleGrid>
                  </Card>
                ))}
                <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} w={180} onClick={() => setFerts([...ferts, emptyFert])}>{t("Dodaj đubrivo")}</Button>
              </>
            )}
            {type === "navodnjavanje" && (
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <Select label={t("Izvor vode")} data={(src.data ?? []).map((x) => x.name)} value={irr.water_source} onChange={(v) => setIrr({ ...irr, water_source: v })} />
                <Select label={t("Sistem")} data={(sys.data ?? []).map((x) => x.name)} value={irr.system} onChange={(v) => setIrr({ ...irr, system: v })} />
                <NumberInput label={t("Zapremina (m³)")} decimalSeparator="," value={irr.volume_m3} onChange={(v) => setIrr({ ...irr, volume_m3: v })} />
                <NumberInput label={t("Norma (mm)")} decimalSeparator="," value={irr.depth_mm} onChange={(v) => setIrr({ ...irr, depth_mm: v })} />
                <NumberInput label={t("Trajanje (h)")} decimalSeparator="," value={irr.duration_h} onChange={(v) => setIrr({ ...irr, duration_h: v })} />
                <Checkbox mt="xl" label={t("Fertirigacija")} checked={irr.fertigation} onChange={(e) => setIrr({ ...irr, fertigation: e.currentTarget.checked })} />
              </SimpleGrid>
            )}
            <Textarea label={t("Opis / napomena")} autosize minRows={2} {...cc("description")} />
            <Group justify="flex-end"><Button onClick={() => save.mutate()} loading={save.isPending}>{t("Sačuvaj upis")}</Button></Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function StornoModal({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => http.post(`/api/v1/diary/entries/${entry.id}/storno`, { reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["diary"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Storno upisa")}>
      <Stack>
        <Text size="sm">{t("Upisi se ne brišu — storno ostaje vidljiv u knjizi i revizijskom tragu.")}</Text>
        <Textarea label={t("Razlog")} required value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end"><Button color="red" onClick={() => m.mutate()} disabled={reason.length < 5} loading={m.isPending}>{t("Storniraj")}</Button></Group>
      </Stack>
    </Modal>
  );
}

const TYPE_COLOR: Record<string, string> = { zastita: "grape", dubrenje: "orange", navodnjavanje: "blue", berba: "green", rezidba: "teal" };

export function DiaryTab({ orchard, season, openNew }: { orchard: Orchard; season: number; openNew?: { key: string; n: number } | null }) {
  const { t } = useT();
  const can = useCan();
  const [type, setType] = useState("sve");
  const [add, setAdd] = useState(false);
  const [initType, setInitType] = useState("zastita");
  useEffect(() => { if (openNew) { setInitType(openNew.key); setAdd(true); } }, [openNew?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const [storno, setStorno] = useState<Entry | null>(null);
  const q = useQuery({ queryKey: ["diary", "entries", orchard.id, season],
    queryFn: () => http.get<Entry[]>(`/api/v1/diary/orchards/${orchard.id}/entries`, { season }) });
  const rows = (q.data ?? []).filter((e) => type === "sve" || e.entry_type === type);
  const cost = rows.filter((e) => e.status === "aktivan").reduce((s, e) => s + Number(e.cost_km ?? 0), 0);
  const detail = (e: Entry) => {
    if (e.ppp.length) return e.ppp.map((p) => `${p.product_name} ${fmtNum(p.dose, 3)} ${p.dose_unit}${p.target_organism ? ` · ${p.target_organism}` : ""}${p.phi_days ? ` · karenca ${p.phi_days} d` : ""}`).join(" + ");
    if (e.fertilization.length) return e.fertilization.map((f) => `${f.product_name} ${fmtNum(f.quantity_per_ha)} ${f.unit}${f.n_kg_ha ? ` (N ${fmtNum(f.n_kg_ha, 1)} kg/ha)` : ""}`).join(" + ");
    if (e.irrigation) return `${fmtNum(e.irrigation.depth_mm, 1)} mm · ${fmtNum(e.irrigation.volume_m3, 1)} m³ · ${e.irrigation.system ?? ""}`;
    return e.description ?? "";
  };
  return (
    <Stack>
      <Group justify="space-between">
        <SegmentedControl size="xs" value={type} onChange={setType} data={[
          { value: "sve", label: t("Sve") }, { value: "zastita", label: t("Zaštita") }, { value: "dubrenje", label: t("Đubrenje") },
          { value: "navodnjavanje", label: t("Navodnjavanje") }, { value: "berba", label: t("Berba") }, { value: "rezidba", label: t("Rezidba") },
          { value: "obrada_tla", label: t("Obrada tla") }]} />
        <Group>
          <Text size="sm" c="dimmed">{t("Troškovi")}: <b>{fmtNum(cost, 0)} KM</b></Text>
          <Tooltip label={t("Obrazac evidencije primjene FFS (Pravilnik BiH 101/12, Prilog I)")}>
            <Button variant="default" size="xs" leftSection={<IconDownload size={14} />}
              onClick={() => downloadFile(`/api/v1/diary/orchards/${orchard.id}/export?season=${season}&fmt=csv`).catch((e) => notifications.show({ color: "red", message: e.message }))}>
              {t("Evidencija FFS (CSV)")}</Button>
          </Tooltip>
          {can("diary:write") && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdd(true)}>{t("Novi upis")}</Button>}
        </Group>
      </Group>
      <DataTable rows={rows as unknown as Record<string, unknown>[]} exportName={`knjiga_${orchard.id}_${season}`} columns={[
        { key: "start_at", label: "Datum", render: (r) => fmtStart(r.start_at) },
        { key: "entry_type", label: "Operacija", render: (r) => <Badge size="sm" variant="light" color={TYPE_COLOR[String(r.entry_type)] ?? "gray"}>{String(r.entry_type)}</Badge> },
        { key: "bbch_code", label: "BBCH", type: "integer" },
        { key: "detalj", label: "Detalji", render: (r) => <Text size="sm">{detail(r as unknown as Entry)}</Text> },
        { key: "area_ha", label: "ha", type: "number" }, { key: "operator_name", label: "Izvršilac" },
        { key: "cost_km", label: "Trošak (KM)", type: "money" },
        { key: "recorded_at", label: "Upisano", render: (r) => {
          const late = (new Date(String(r.recorded_at)).getTime() - new Date(String(r.start_at)).getTime()) / 864e5 > 30;
          return <Text size="xs" c={late ? "red" : "dimmed"} title={late ? t("Upisano više od 30 dana nakon operacije") : ""}>{fmtDateTime(r.recorded_at)}</Text>;
        } },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
        { key: "akcije", label: "", render: (r) => (can("diary:write") && r.status === "aktivan"
          ? <Tooltip label={t("Storno")}><ActionIcon size="sm" variant="subtle" color="red" onClick={(e) => { e.stopPropagation(); setStorno(r as unknown as Entry); }}><IconX size={14} /></ActionIcon></Tooltip> : null) },
      ]} />
      {add && <EntryModal orchard={orchard} initialType={initType} onClose={() => setAdd(false)} />}
      {storno && <StornoModal entry={storno} onClose={() => setStorno(null)} />}
    </Stack>
  );
}
